import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const navLinks = [
  { label: "Home", href: "/", type: "route" },
  { label: "Services", href: "/services", type: "route" },
  { label: "About", href: "/about", type: "route" },
  { label: "Locations", href: "/location", type: "route" },
  { label: "Contact", href: "#contact", type: "anchor" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleAnchorClick = (href: string) => {
    setMenuOpen(false);
    if (href === "#contact") {
      const el = document.querySelector("#contact");
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
      } else {
        navigate("/");
        setTimeout(() => {
          const target = document.querySelector("#contact");
          if (target) target.scrollIntoView({ behavior: "smooth" });
        }, 400);
      }
    }
  };

  const isActive = (href: string) => {
    if (href === "/") return location.pathname === "/";
    return location.pathname.startsWith(href);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-charcoal/97 backdrop-blur-md border-b border-white/5"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between h-20">
        {/* Logo */}
        <Link to="/" className="flex flex-col leading-none cursor-pointer">
          <span className="font-serif text-white text-xl font-bold tracking-widest uppercase">
            Don Collins
          </span>
          <span className="font-sans text-gold text-xs tracking-[0.25em] uppercase font-medium mt-0.5">
            Auto Storage
          </span>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-8">
          {navLinks.map((link) =>
            link.type === "route" ? (
              <Link
                key={link.label}
                to={link.href}
                className={`font-sans text-sm tracking-wide transition-colors duration-200 cursor-pointer whitespace-nowrap ${
                  isActive(link.href)
                    ? "text-gold"
                    : "text-[#B8B8B8] hover:text-gold"
                }`}
              >
                {link.label}
              </Link>
            ) : (
              <button
                key={link.label}
                onClick={() => handleAnchorClick(link.href)}
                className="font-sans text-sm text-[#B8B8B8] hover:text-gold tracking-wide transition-colors duration-200 cursor-pointer whitespace-nowrap"
              >
                {link.label}
              </button>
            )
          )}
        </nav>

        {/* CTA */}
        <div className="hidden lg:flex items-center gap-4">
          <a
            href="tel:4154539180"
            className="font-sans text-sm text-gold flex items-center gap-2 hover:text-gold-light transition-colors duration-200 cursor-pointer whitespace-nowrap"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-phone-line text-sm"></i>
            </div>
            (415) 453-9180
          </a>
          <button
            onClick={() => handleAnchorClick("#contact")}
            className="font-sans text-sm bg-gold text-charcoal font-semibold px-5 py-2.5 rounded tracking-wide hover:bg-gold-light transition-colors duration-200 cursor-pointer whitespace-nowrap"
          >
            Reserve Storage
          </button>
        </div>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setMenuOpen(!menuOpen)}
          className="lg:hidden w-8 h-8 flex items-center justify-center text-white cursor-pointer"
          aria-label="Toggle menu"
        >
          <div className="w-6 h-6 flex items-center justify-center">
            <i className={`${menuOpen ? "ri-close-line" : "ri-menu-line"} text-xl`}></i>
          </div>
        </button>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="lg:hidden bg-charcoal-100 border-t border-white/10">
          <div className="px-6 py-6 flex flex-col gap-4">
            {navLinks.map((link) =>
              link.type === "route" ? (
                <Link
                  key={link.label}
                  to={link.href}
                  onClick={() => setMenuOpen(false)}
                  className={`font-sans text-sm tracking-wide text-left transition-colors cursor-pointer whitespace-nowrap ${
                    isActive(link.href) ? "text-gold" : "text-[#B8B8B8] hover:text-gold"
                  }`}
                >
                  {link.label}
                </Link>
              ) : (
                <button
                  key={link.label}
                  onClick={() => handleAnchorClick(link.href)}
                  className="font-sans text-sm text-[#B8B8B8] hover:text-gold tracking-wide text-left transition-colors cursor-pointer whitespace-nowrap"
                >
                  {link.label}
                </button>
              )
            )}
            <div className="pt-4 border-t border-white/10">
              <a
                href="tel:4154539180"
                className="block font-sans text-sm text-gold mb-3 cursor-pointer"
              >
                (415) 453-9180
              </a>
              <button
                onClick={() => handleAnchorClick("#contact")}
                className="w-full font-sans text-sm bg-gold text-charcoal font-semibold px-5 py-3 rounded tracking-wide cursor-pointer whitespace-nowrap"
              >
                Reserve Storage
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
